import { useState } from 'react';
import {
  Select,
  Grid,
  MenuItem,
  Typography,
} from '@forgeglobal/design-framework';
import DocumentsTableHeader from '@tcs/client-portal-common/components/molecules/DocumentsTableHeader';
import Spinner from '@tcs/common/components/atoms/Spinner';
import useFilteredDocuments from '@tcs/client-portal-common/hooks/useStatements';
import { DocumentType } from '@tcs/client-portal-api/platform_generated';
import { useStyles } from './DocumentDetails.style';
import StatementDetails from '@tcs/client-portal-common/components/templates/StatementDetails';
import TaxDetails from '@tcs/client-portal-common/components/templates/TaxDetails';

export interface Props {
  accountNumber?: string;
  title?: string;
  type?: DocumentType;
  onlyActiveAccounts?: boolean;
}

export const DocumentDetails = ({
  accountNumber,
  title,
  type,
  onlyActiveAccounts,
}: Props) => {
  const classes = useStyles();
  const [selectedType, setSelectedType] = useState(
    type || DocumentType.STATEMENT
  );

  const {
    loading,
    documents,
    accountNumbers,
    years,
    selectedAccount,
    selectedYear,
    setSelectedAccount,
    setSelectedYear,
  } = useFilteredDocuments(selectedType, accountNumber, onlyActiveAccounts);

  if (loading) {
    return <Spinner />;
  }

  if (!documents) {
    return null;
  }

  const controls = [
    {
      name: 'documents',
      component: !type && (
        <Select
          data-test-id="type-dropdown"
          label="Document"
          onDelete={null}
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value as DocumentType)}
          className={classes.dropdown}>
          <MenuItem value={DocumentType.STATEMENT}>
            {DocumentType.STATEMENT}
          </MenuItem>
          <MenuItem value={DocumentType.TAX}>{DocumentType.TAX}</MenuItem>
        </Select>
      ),
    },
    {
      name: 'accounts',
      component: !accountNumber && (
        <Select
          data-test-id="account-dropdown"
          label="Account"
          onDelete={null}
          value={selectedAccount}
          onChange={(e) =>
            setSelectedAccount((e.target as HTMLTextAreaElement).value)
          }
          className={classes.dropdown}>
          {accountNumbers.map((a) => (
            <MenuItem key={a} value={a}>
              {a}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      name: 'years',
      component: (
        <Select
          data-test-id="year-dropdown"
          label={selectedType === 'Tax' ? 'Tax Year' : 'Year'}
          onDelete={null}
          value={selectedYear}
          onChange={(e) =>
            setSelectedYear((e.target as HTMLTextAreaElement).value)
          }
          className={classes.dropdown}>
          {years.map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </Select>
      ),
    },
  ];

  const headerControls = controls
    .map((control) => control.component)
    .filter((component) => !!component);

  return (
    <Grid container>
      <Grid item container spacing={4} className={classes.selectionsHeader}>
        {title && (
          <Grid item className={classes.title}>
            <Typography variant="h5" data-test-id="documents-table-title">
              {title}
            </Typography>
          </Grid>
        )}
        <DocumentsTableHeader items={headerControls} />
      </Grid>
      {selectedType === 'Statement' ? (
        <StatementDetails
          documents={documents}
          selectedAccount={selectedAccount}
          loading={loading}
        />
      ) : (
        <TaxDetails
          documents={documents}
          selectedAccount={selectedAccount}
          loading={loading}
          selectedYear={selectedYear}
        />
      )}
    </Grid>
  );
};

export default DocumentDetails;
